// src/api/axios.js
import axios from 'axios';

const baseURL =
  process.env.REACT_APP_API_BASE ||
  '/api/v1';

console.log(
  'API baseURL:',
  process.env.REACT_APP_API_BASE,
);

const API = axios.create({
  baseURL,

  /**
   * refresh token 在 httpOnly cookie 中，
   * 所以所有请求都允许携带 cookie。
   */
  withCredentials: true,

  headers: {
    'Content-Type':
      'application/json',
  },
});


/**
 * 由外部注入。
 *
 * 这样 axios.js 不直接 import Redux store，
 * 避免循环依赖。
 */
let getToken =
  () => null;

let onRefreshSuccess =
  (_newToken) => { };

let onForceLogout =
  () => { };


export function setAuthHandlers(
  handlers = {},
) {
  getToken =
    handlers.getToken ||
    getToken;

  onRefreshSuccess =
    handlers.onRefreshSuccess ||
    onRefreshSuccess;

  onForceLogout =
    handlers.onForceLogout ||
    onForceLogout;
}


/**
 * URL pathname helper。
 *
 * config.url 可能是：
 *
 * /users/me
 * /auth/refresh
 * https://xxx/api/v1/users/me
 *
 * 所以统一转 pathname。
 */
const toPathname = (url) => {
  try {
    const base =
      typeof window !==
        'undefined' &&
        window.location?.origin
        ? window.location.origin
        : 'http://localhost';

    return new URL(
      url,
      base,
    ).pathname.toLowerCase();
  } catch {
    return (
      url || ''
    ).toLowerCase();
  }
};


/**
 * 鉴权接口本身不能继续触发 refresh，
 * 否则可能产生循环。
 */
const AUTH_PATH_RE =
  /\/auth\/(login|refresh|logout|logout-anyway|forgot-password|reset-password)$/;


/**
 * 请求拦截器。
 *
 * accessToken 只存在 Redux 内存，
 * 不存 localStorage/sessionStorage。
 */
API.interceptors.request.use(
  (config) => {
    const token =
      getToken();

    const pathname =
      toPathname(
        config.url,
      );

    const isAuthPath =
      AUTH_PATH_RE.test(
        pathname,
      );

    if (
      token &&
      !isAuthPath
    ) {
      config.headers =
        config.headers ??
        {};

      config.headers.Authorization =
        `Bearer ${token}`;
    }

    return config;
  },
);


/**
 * 所有 refresh 共用一个 Promise。
 *
 * 两种场景都会调用：
 *
 * 1. 普通 API 返回 401
 * 2. 新标签页启动时主动恢复 session
 *
 * 避免同时发送多个 /auth/refresh。
 */
let refreshPromise = null;


/**
 * 主动刷新 access token。
 *
 * 这个函数除了给 Axios 401 interceptor 用，
 * PrivateRoute 也会在新标签页启动时调用。
 */
export const refreshAccessTokenRequest =
  async () => {
    if (!refreshPromise) {
      refreshPromise =
        API.post(
          '/auth/refresh',
        )
          .then(
            ({
              data,
            }) => {
              const newAccessToken =
                data?.accessToken ||
                null;

              if (
                !newAccessToken
              ) {
                throw new Error(
                  'No accessToken in refresh response',
                );
              }

              /**
               * 通知 Redux：
               * 更新内存中的 accessToken。
               */
              onRefreshSuccess(
                newAccessToken,
              );

              return newAccessToken;
            },
          )
          .catch(
            async (
              refreshError,
            ) => {
              /**
               * refresh cookie 已失效。
               *
               * 尽量让后端清理 cookie，
               * 然后清理前端 auth state。
               */
              try {
                await API
                  .post(
                    '/auth/logout-anyway',
                  )
                  .catch(
                    () => { },
                  );
              } finally {
                onForceLogout();
              }

              throw refreshError;
            },
          )
          .finally(() => {
            refreshPromise =
              null;
          });
    }

    return refreshPromise;
  };


/**
 * Response interceptor。
 *
 * 普通 API 返回 401：
 *
 * request
 *   ↓
 * 401
 *   ↓
 * refresh
 *   ↓
 * 更新 Redux accessToken
 *   ↓
 * 重放原请求
 */
API.interceptors.response.use(
  (response) =>
    response,

  async (error) => {
    const originalRequest =
      error.config ||
      {};

    const status =
      error.response?.status;

    const pathname =
      toPathname(
        originalRequest.url,
      );

    const isAuthPath =
      AUTH_PATH_RE.test(
        pathname,
      );


    /**
     * 不是 401：
     * 正常向上抛出。
     *
     * auth 接口自己出错：
     * 也不能继续 refresh。
     */
    if (
      status !== 401 ||
      isAuthPath
    ) {
      return Promise.reject(
        error,
      );
    }


    /**
     * 避免同一请求无限 refresh。
     */
    if (
      originalRequest._retry
    ) {
      onForceLogout();

      return Promise.reject(
        error,
      );
    }

    originalRequest._retry =
      true;


    try {
      const newAccessToken =
        await refreshAccessTokenRequest();

      /**
       * 优先读取 Redux 当前 token。
       *
       * 如果 Redux 更新还没反映，
       * 使用刚 refresh 返回的 token。
       */
      const token =
        getToken() ||
        newAccessToken;

      originalRequest.headers =
        originalRequest.headers ??
        {};

      if (token) {
        originalRequest
          .headers
          .Authorization =
          `Bearer ${token}`;
      }

      /**
       * 重放原请求。
       */
      return API.request(
        originalRequest,
      );
    } catch (
    refreshError
    ) {
      return Promise.reject(
        refreshError,
      );
    }
  },
);


export default API;